import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchedPlayer,
  fetchAccessibleBoards,
  accessibleBoardsByDate,
  hasCurrentActions,
  saveRegistrations,
  hasRegistrations,
  prices,
  getPlayerLoading,
  getPaymentLoading,
  createPayment,
  fetchPlayerFromLicence,
  cleanState,
  fetchedNoPlayer,
  fetchPlayerById,
  getPlayersWithRegistrations,
  syncSelectedBoardsWithParticipations,
  getAccessibleBoards,
  getAllowAdminDailyLimitBypass,
  getAllowAdminWaitlistBypass,
  getSaveRegistrationsLoading,
  setAllowAdminDailyLimitBypass,
  setAllowAdminWaitlistBypass,
} from "../../features/player/playerSlice";
import {
  fetchBoards,
  fetchTournoi,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import { admin } from "../../features/session/sessionSlice";
import { isPreRegistrationEntryFlow } from "../../utils/registerFlow";
import { isBlockedByWomenOnlyTournoi } from "../../utils/tournoiEligibility";

import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store";
import { RegisterListBoardsDate } from "./RegisterListBoardsDate";
import ConfirmDialog from "../Base/ConfirmDialog";
import PageLoader from "../Base/PageLoader";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Zoom,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { calendarDayKey } from "../../features/player/playerHelper";
import { currentDate } from "../../helpers/currentDate";
import { DialogTextActionButton } from "../Base/DialogTextActionButton";

interface RegisterSelectProps {}

const RegisterSelect: FC<RegisterSelectProps> = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const player = useAppSelector(fetchedPlayer);
  const accessibleBoardByDate = useAppSelector(accessibleBoardsByDate);
  const hasCurrentActionsState = useAppSelector(hasCurrentActions);
  const pricesState = useAppSelector(prices);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const isAdmin = useAppSelector(admin);
  const isPreRegistrationEntry = isPreRegistrationEntryFlow();
  const entryRoute =
    !isAdmin && isPreRegistrationEntry
      ? "/pre-registration"
      : "/register";
  const playerHasRegistrations = useAppSelector(hasRegistrations);
  const loading = useAppSelector(getPlayerLoading);
  const paymentLoading = useAppSelector(getPaymentLoading);
  const accessibleBoards = useAppSelector(getAccessibleBoards);
  const allowAdminDailyLimitBypass = useAppSelector(
    getAllowAdminDailyLimitBypass
  );
  const allowAdminWaitlistBypass = useAppSelector(getAllowAdminWaitlistBypass);
  const saveRegistrationsLoading = useAppSelector(getSaveRegistrationsLoading);
  const selectedBoardIds = useAppSelector((state) => state.player.selectedBoardIds);
  const adminSaveInFlightRef = useRef(false);
  const [searchParams] = useSearchParams();
  const playerIdFromUrl = searchParams.get("playerId");
  const licenceIdFromUrl = searchParams.get("licenceId");
  const noPlayer = useAppSelector(fetchedNoPlayer);
  const playersWithRegistrationsRaw = useAppSelector(
    getPlayersWithRegistrations
  );
  const [confirmAdminFullBoardOpen, setConfirmAdminFullBoardOpen] =
    useState<boolean>(false);
  const [adminFullBoardNames, setAdminFullBoardNames] = useState<string[]>([]);
  const [confirmAutoCheckInOpen, setConfirmAutoCheckInOpen] = useState(false);
  const [todayBoardNamesForCheckIn, setTodayBoardNamesForCheckIn] = useState<
    string[]
  >([]);
  /** Couvre sauvegarde + rechargement joueur/tableaux (évite le spinner plein écran). */
  const [adminSaveInProgress, setAdminSaveInProgress] = useState(false);
  /** Premier chargement des tableaux accessibles pour le joueur courant. */
  const [boardsReady, setBoardsReady] = useState(false);

  // Mémoriser playersWithRegistrations pour éviter les re-rendus constants
  const playersWithRegistrations = useMemo(() => {
    return playersWithRegistrationsRaw;
  }, [playersWithRegistrationsRaw]);

  useEffect(() => {
    if (playerIdFromUrl) {
      const localPlayer = playersWithRegistrations.find(
        (p) => p.id === playerIdFromUrl
      );
      if (localPlayer && (!player || player.id !== playerIdFromUrl)) {
        dispatch({
          type: "player/fetchPlayerById/fulfilled",
          payload: localPlayer,
        });
      } else if (!localPlayer) {
        dispatch(cleanState());
        dispatch(fetchPlayerById(playerIdFromUrl));
      }
    } else if (
      licenceIdFromUrl &&
      (!player || player.licenceId !== licenceIdFromUrl)
    ) {
      dispatch(cleanState());
      dispatch(fetchPlayerFromLicence(licenceIdFromUrl));
    }
    if (!player && !loading && !playerIdFromUrl && !licenceIdFromUrl) {
      navigate(entryRoute);
    }
  }, [
    dispatch,
    navigate,
    player,
    playerIdFromUrl,
    licenceIdFromUrl,
    loading,
    playersWithRegistrations,
    entryRoute,
  ]);

  const blockedByWomenOnly = isBlockedByWomenOnlyTournoi(tournoi, player);

  useEffect(() => {
    void dispatch(fetchTournoi());
  }, [dispatch]);

  useEffect(() => {
    if (player && blockedByWomenOnly) {
      navigate(entryRoute, {
        replace: true,
        state: { womenOnlyBlocked: true },
      });
      return;
    }
    if (!player?.id || blockedByWomenOnly) {
      setBoardsReady(false);
      return;
    }
    let cancelled = false;
    setBoardsReady(false);
    void dispatch(fetchAccessibleBoards(player.id)).finally(() => {
      if (!cancelled) {
        setBoardsReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    player,
    blockedByWomenOnly,
    navigate,
    entryRoute,
  ]);

  useEffect(() => {
    if (!isAdmin && allowAdminDailyLimitBypass) {
      dispatch(setAllowAdminDailyLimitBypass(false));
    }
  }, [allowAdminDailyLimitBypass, dispatch, isAdmin]);

  useEffect(() => {
    if (!isAdmin && allowAdminWaitlistBypass) {
      dispatch(setAllowAdminWaitlistBypass(false));
    }
  }, [allowAdminWaitlistBypass, dispatch, isAdmin]);

  /** Inscription sans Stripe (admin, ou joueur avec uniquement des tableaux offerts). */
  const saveRegistrationsWithoutPayment = async (
    autoCheckInTodayBoards: boolean
  ) => {
    if (
      adminSaveInFlightRef.current ||
      saveRegistrationsLoading ||
      adminSaveInProgress
    ) {
      return;
    }
    adminSaveInFlightRef.current = true;
    setAdminSaveInProgress(true);
    try {
      const resultAction = await dispatch(
        saveRegistrations({ autoCheckInTodayBoards })
      );
      if (saveRegistrations.fulfilled.match(resultAction)) {
        if (player?.id) {
          await dispatch(fetchAccessibleBoards(player.id));
        }
        if (player?.licenceId) {
          await dispatch(fetchPlayerFromLicence(player.licenceId));
        }
        await dispatch(fetchBoards());
        navigate("/register/validation");
      }
    } finally {
      adminSaveInFlightRef.current = false;
      setAdminSaveInProgress(false);
    }
  };

  const isAdminSaveBusy = adminSaveInProgress || saveRegistrationsLoading;
  const isFreeOnlySelection = pricesState.newPrice <= 0;

  /** Nouveaux tableaux sélectionnés dont la date est aujourd'hui. */
  const getTodayBoardsBeingAdded = (): string[] => {
    const alreadyRegistered = new Set(
      (player?.participation ?? [])
        .map((p) => p.tableauId)
        .filter((id): id is string => Boolean(id))
        .map(String)
    );
    const todayKey = calendarDayKey(currentDate());
    return selectedBoardIds
      .filter((id) => !alreadyRegistered.has(String(id)))
      .map((id) => accessibleBoards.find((b) => String(b.id) === String(id)))
      .filter((board): board is NonNullable<typeof board> => {
        if (!board?.date) return false;
        return calendarDayKey(board.date) === todayKey;
      })
      .map((board) => board.name || board.label || String(board.id));
  };

  const proceedSaveWithoutPaymentAfterGuards = async () => {
    const todayNames = getTodayBoardsBeingAdded();
    if (todayNames.length > 0 && isAdmin) {
      setTodayBoardNamesForCheckIn(todayNames);
      setConfirmAutoCheckInOpen(true);
      return;
    }
    await saveRegistrationsWithoutPayment(false);
  };

  const proceedAdminSaveAfterGuards = proceedSaveWithoutPaymentAfterGuards;

  const onAdminSaveClick = async () => {
    if (isAdminSaveBusy) {
      return;
    }
    const fullOrClosedBoardNames = selectedBoardIds
      .map((id) => accessibleBoards.find((board) => board.id === id))
      .filter((board) => board && (board.isFull || board.ferme))
      .map((board) => board!.name);

    if (fullOrClosedBoardNames.length > 0) {
      setAdminFullBoardNames(fullOrClosedBoardNames);
      setConfirmAdminFullBoardOpen(true);
      return;
    }

    await proceedAdminSaveAfterGuards();
  };

  // Synchroniser selectedBoardIds avec les vraies participations après chargement du joueur
  useEffect(() => {
    if (player && player.participation) {
      dispatch(syncSelectedBoardsWithParticipations());
    }
  }, [dispatch, player]);

  if (!loading && playerIdFromUrl && noPlayer) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">
          Aucun joueur trouvé pour cet identifiant.
        </Alert>
      </Box>
    );
  }

  const showInitialPageLoader =
    !adminSaveInProgress &&
    ((loading && !player) ||
      (Boolean(player) && !blockedByWomenOnly && !boardsReady));

  if (showInitialPageLoader) {
    return (
      <PageLoader
        message={
          player
            ? "Chargement des tableaux…"
            : "Chargement de l’inscription…"
        }
      />
    );
  }

  return (
    <>
      <Zoom in>
        <Box width="100%" maxWidth={800} mx="auto" px={{ xs: 1, sm: 0 }}>
        <Stack spacing={4} alignItems="center">
          {!isAdmin && playerHasRegistrations && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                borderColor: "warning.main",
                borderLeft: `4px solid ${theme.palette.warning.main}`,
                bgcolor: isDark
                  ? alpha(theme.palette.warning.main, 0.14)
                  : "warning.light",
                color: isDark ? theme.palette.warning.light : "warning.contrastText",
                fontWeight: 500,
                width: "100%",
                maxWidth: 800,
              }}
            >
              Vous êtes déjà inscrit à au moins un tableau. Pour toute
              modification, contactez-nous par mail à l'adresse{" "}
              <a
                href={`mailto:${tournoi?.mail}`}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                {tournoi?.mail}
              </a>
            </Alert>
          )}

          <Alert
            severity="info"
            variant="outlined"
            sx={{
              width: "100%",
              maxWidth: 800,
              borderLeft: `4px solid ${theme.palette.secondary.main}`,
              ...(isDark
                ? {
                    borderColor: alpha(theme.palette.secondary.light, 0.45),
                    bgcolor: alpha(theme.palette.secondary.main, 0.12),
                    color: theme.palette.grey[50],
                    "& .MuiAlert-icon": { color: theme.palette.secondary.light },
                  }
                : {
                    borderColor: alpha(theme.palette.secondary.main, 0.4),
                    bgcolor: alpha(theme.palette.secondary.main, 0.06),
                    "& .MuiAlert-icon": { color: theme.palette.secondary.dark },
                  }),
            }}
          >
            Sélectionnez les tableaux sur lesquels vous souhaitez vous inscrire.
          </Alert>

          {isAdmin && isAdminSaveBusy && (
            <Alert severity="info" sx={{ width: "100%", maxWidth: 800 }}>
              Enregistrement en cours…
            </Alert>
          )}

          {isAdmin && (
            <Box sx={{ width: "100%" }}>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowAdminDailyLimitBypass}
                      onChange={(event) =>
                        dispatch(
                          setAllowAdminDailyLimitBypass(event.target.checked)
                        )
                      }
                      color="warning"
                    />
                  }
                  label="Autoriser plus de 2 tableaux par jour"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowAdminWaitlistBypass}
                      onChange={(event) =>
                        dispatch(
                          setAllowAdminWaitlistBypass(event.target.checked)
                        )
                      }
                      color="warning"
                    />
                  }
                  label="Contourner la règle liste d'attente / places (inscription directe)"
                />
              </Stack>
            </Box>
          )}

          {Object.keys(accessibleBoardByDate)
            .sort()
            .map((date) => (
              <RegisterListBoardsDate
                key={date}
                date={date}
                accessibleBoards={accessibleBoardByDate[date]}
              />
            ))}

          {isAdmin ? (
            <Box sx={{ width: "98%" }}>
              <Stack
                spacing={3}
                alignItems="center"
                justifyContent="center"
                direction="row"
              >
                <Chip
                  label={`Déjà payé ${pricesState.formerPrice} €`}
                  color="secondary"
                  variant="outlined"
                  sx={(t) => ({
                    fontWeight: 600,
                    ...(t.palette.mode === "dark" && {
                      borderColor: alpha(t.palette.secondary.light, 0.65),
                      color: t.palette.secondary.light,
                    }),
                  })}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!hasCurrentActionsState || isAdminSaveBusy}
                  onClick={onAdminSaveClick}
                  aria-busy={isAdminSaveBusy}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 280,
                    "&.Mui-disabled": isDark
                      ? {
                          backgroundColor: theme.palette.grey[800],
                          color: theme.palette.grey[50],
                        }
                      : {
                          color: theme.palette.grey[700],
                          backgroundColor: theme.palette.grey[300],
                        },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isAdminSaveBusy && (
                      <CircularProgress size={22} color="inherit" />
                    )}
                    {isAdminSaveBusy
                      ? "Enregistrement en cours…"
                      : isFreeOnlySelection && pricesState.priceDifference <= 0
                        ? "Sauvegarder les inscriptions (gratuit)"
                        : `Sauvegarder les inscriptions ${pricesState.priceDifference} €`}
                  </Box>
                </Button>
                <Chip
                  label={
                    isFreeOnlySelection
                      ? "Nouveau prix gratuit"
                      : `Nouveau prix ${pricesState.newPrice} €`
                  }
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Box>
          ) : (
            <>
              <Alert
                severity="warning"
                variant="standard"
                icon={false}
                sx={{
                  border: "1px solid",
                  borderColor: "warning.main",
                  borderLeft: `4px solid ${theme.palette.warning.main}`,
                  fontWeight: 500,
                  ...(isDark && {
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.grey[100],
                  }),
                }}
              >
                Attention, le remboursement n'est possible que sur présentation
                d'un certificat médical.
              </Alert>
              {!(!isAdmin && playerHasRegistrations) && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    !hasCurrentActionsState ||
                    paymentLoading ||
                    (isFreeOnlySelection && isAdminSaveBusy)
                  }
                  onClick={async () => {
                    if (isFreeOnlySelection) {
                      await proceedSaveWithoutPaymentAfterGuards();
                      return;
                    }
                    await dispatch(createPayment("SAVE"));
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    ...(isDark && {
                      "&.Mui-disabled": {
                        opacity: 1,
                        bgcolor: theme.palette.grey[800],
                        color: theme.palette.grey[50],
                      },
                    }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {isFreeOnlySelection
                      ? isAdminSaveBusy
                        ? "Inscription en cours…"
                        : "Confirmer l'inscription (gratuit)"
                      : `Payer l'inscription ${pricesState.newPrice} €`}
                    {(paymentLoading ||
                      (isFreeOnlySelection && isAdminSaveBusy)) && (
                      <CircularProgress
                        color="info"
                        size={24}
                        sx={{ marginLeft: 1 }}
                      />
                    )}
                  </Box>
                </Button>
              )}
            </>
          )}
        </Stack>
        </Box>
      </Zoom>
      <ConfirmDialog
        open={confirmAdminFullBoardOpen}
        title="Confirmer l'inscription"
        message={`Vous allez inscrire ce joueur sur un tableau déjà complet ou fermé (${adminFullBoardNames.join(", ")}). Voulez-vous continuer ?`}
        confirmLabel="Oui, continuer"
        cancelLabel="Annuler"
        confirmColor="warning"
        onConfirm={() => {
          setConfirmAdminFullBoardOpen(false);
          if (!isAdminSaveBusy) {
            void proceedAdminSaveAfterGuards();
          }
        }}
        onCancel={() => setConfirmAdminFullBoardOpen(false)}
      />
      <Dialog
        open={confirmAutoCheckInOpen}
        onClose={() => setConfirmAutoCheckInOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Pointer automatiquement ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {todayBoardNamesForCheckIn.length === 1
              ? `Le tableau « ${todayBoardNamesForCheckIn[0]} » a lieu aujourd'hui. Voulez-vous le pointer automatiquement à l'inscription ?`
              : `Ces tableaux ont lieu aujourd'hui : ${todayBoardNamesForCheckIn.join(", ")}. Voulez-vous les pointer automatiquement à l'inscription ?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
          <DialogTextActionButton
            onClick={() => setConfirmAutoCheckInOpen(false)}
            variant="outlined"
          >
            Annuler
          </DialogTextActionButton>
          <Button
            variant="outlined"
            disabled={isAdminSaveBusy}
            onClick={() => {
              setConfirmAutoCheckInOpen(false);
              void saveRegistrationsWithoutPayment(false);
            }}
          >
            Non, sans pointage
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isAdminSaveBusy}
            onClick={() => {
              setConfirmAutoCheckInOpen(false);
              void saveRegistrationsWithoutPayment(true);
            }}
          >
            Oui, pointer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterSelect;
